<template>
  <div class="invoice-form">
    <form @submit.stop.prevent="form.handleSubmit()">
      <!-- invoice details -->
      <div class="grid grid-cols-2 gap-8">
        <div>
          <form.Field
            name="projectId"
            :validators="{
              onBlur: yup.string().required('Project is required')
            }"
          >
            <template #default="{ field }">
              <BaseSelect
                :model-value="(field.state.value as string)"
                :name="field.name"
                :options="projects"
                label="Project"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => {field.handleChange(e.target.value); selectedProjectId = e.target.value}"
              />
            </template>
          </form.Field>
        </div>

        <div>
          <form.Field
            name="invoiceId"
            :validators="{
              onBlur: yup.string().required('Invoice # is required')
            }"
          >
            <template #default="{ field }">
              <BaseInput
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Invoice #"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-8">
        <div>
          <form.Field
            name="dueDate"
            :validators="{
              onBlur: yup.string().required('Due Date is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Due date"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>

        <div>
          <form.Field
            name="invoiceDate"
            :validators="{
              onBlur: yup.string().required('Invoice Date is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Invoice Date"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <!-- items -->
      <h4 class="mt-6">Items</h4>
      <hr class="mt-2">

      <div v-if="lineItemsLoading" class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
        <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
        <p class="text-sm text-center text-slate-500 pt-4">
          Loading...
        </p>
      </div>

      <div v-else>
        <form.Field
          name="items"
          :validators="{
            onBlur: yup.array().min(1, 'At least one invoice item is required')
          }"
        >
          <template #default="{ field }">
            <div
              v-if="(field.state.meta.errors && field.state.meta.errors[0]) && form.getFieldValue('items').length === 0"
              class="text-base/6 text-error sm:text-sm/6 mt-2 flex flex-col items-center"
            >
              <span class="i-heroicons-exclaimation-circle size-8" />
              <p class="text-base/6 text-error sm:text-sm/6">
                {{ field.state.meta.errors[0] }}
              </p>
            </div>
          </template>
        </form.Field>

        <div
          v-for="(item, i) in invoiceItems"
          :key="item.id"
          :class="{ 'mt-8': i !== 0, 'mt-4': i === 0 }"
        >
          <div>
            <h3>{{ item.title }}</h3>
          </div>

          <div class="mt-2">
            <span v-html="item.scopeOfWork" />
          </div>

          <hr class="w-3/4 mt-8 mb-4">

          <div class="grid grid-cols-2">
            <div>
              <span class="font-semibold mr-2">Quantity:</span> <span>{{ item.quantity }}</span>
            </div>

            <div>
              <span class="font-semibold mr-2">Unit Price:</span> <span>{{ currency(item.unitPrice)}}</span>
            </div>

            <div class="mt-4">
              <span class="font-semibold mr-2">Total Price:</span> <span>{{ currency(item.totalPrice) }}</span>
            </div>
          </div>

          <hr class="my-4">
        </div>
      </div>

      <!-- select items button -->
      <div v-if="!lineItemsLoading" class="my-8 flex justify-center items-center">
        <BaseButton
          type="button"
          style="box-shadow: none !important;"
          :disabled="!isProjectSelected || lineItemsLoading"
          :class="{ 'opacity-50': !isProjectSelected || lineItemsLoading }"
          @click="showModal = true"
        >
          <span class="i-heroicons-plus-circle size-8" />
          <br>
          <p class="text-lg">Select invoice items</p>
        </BaseButton>
      </div>

      <!-- total, credit, balance -->
      <div class="mt-5 ml-auto min-w-[30%] max-w-fit">
        <div class="bg-slate-100 p-4 rounded-lg gap-4 text-right">
          <div class="grid grid-cols-2">
            <span class="font-semibold">Credit:</span> <span>{{ currency(form.getFieldValue('credit')) }}</span>
          </div>
          <div class="grid grid-cols-2">
            <span class="font-semibold">Balance:</span> <span>{{ currency(form.getFieldValue('balance')) }}</span>
          </div>
          <div class="grid grid-cols-2">
            <span class="font-semibold">Total:</span> <span>{{ currency(form.getFieldValue('total')) }}</span>
          </div>
        </div>
      </div>

      <!-- submit button -->
      <hr class="mt-6">
      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              variant="base"
              text="Cancel"
              :to="backTo ? { path: backTo } : { name: 'ListInvoices' }"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
            />

            <BaseButton
              variant="primary"
              text="Save"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>

    <!-- select items modal -->
    <Modal
      v-model="showModal"
      title="Select Invoice Items"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
      width="w-full md:w-[90%] lg:w-[80%] xl:w-[60%]"
    >
      <template #body>
        <div class="grid grid-cols-3 gap-4 mt-6">
          <div class="flex flex-col items-center">
            <div class="flex items-center">
              <span class="i-heroicons-currency-dollar size-5 mr-2"/>
              <h3>Job Total Value</h3>
            </div>
            <p class="text-base mt-2">{{ currency(jobsTotalValue) }}</p>
          </div>

          <div class="flex flex-col items-center">
            <div class="flex items-center">
              <span class="i-heroicons-arrow-right-circle size-5 mr-2"/>
              <h3>Previously Invoiced</h3>
            </div>
            <p class="text-base mt-2">{{ currency(invoicedValue.previouslyInvoicedValue) }} <span v-if="invoicedValue.previouslyInvoicedCredit" class="text-error text-xs">(-{{ currency(invoicedValue.previouslyInvoicedCredit) }})</span></p>
          </div>

          <div class="flex flex-col items-center">
            <div class="flex items-center">
              <span class="i-heroicons-calculator size-5 mr-2"/>
              <h3>This Invoice</h3>
            </div>
            <p class="text-base mt-2" :class="Math.abs(thisInvoice.value) + Math.abs(invoicedValue.previouslyInvoicedValue) > jobsTotalValue ? 'text-error' : 'text-success'">
              {{ currency(thisInvoice.value) }}
            </p>
            <div class="text-xs">
              <span>{{ currency(thisInvoice.absValue - thisInvoice.credit) }}</span>/<span class="text-error">{{ currency(thisInvoice.credit) }}</span>
            </div>
          </div>
        </div>

        <div class="mt-8">
          <BaseSelect
            v-model="selectedMethod"
            :options="selectOptions"
          />
        </div>

        <form @submit.stop.prevent>
          <div v-if="selectedMethod === '1'">
            <itemsForm.Field name="methodOneItems">
              <template #default>
                <BaseTable
                  :fields="methodOneTableFields"
                  :items="itemsForm.getFieldValue('methodOneItems')"
                >
                  <template #arrow-header>
                    <div class="text-center">
                      <span class="i-heroicons-arrow-right size-4"/>
                    </div>
                  </template>
                  <template #arrow-data>
                    <div class="text-center">
                      <span class="i-heroicons-arrow-right size-4"/>
                    </div>
                  </template>

                  <template #title-data="{ row, index }">
                    <div v-if="row.id.toString().includes('credit')">
                      <itemsForm.Field
                        :name="`methodOneItems[${index}].title`"
                        :validators="{
                          onBlur: yup.string().required('Title is required')
                        }"
                      >
                        <template #default="{ field: subField }">
                          <BaseInput
                            :model-value="(subField.state.value as string)"
                            :name="subField.name"
                            :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                            @blur="subField.handleBlur"
                            @input="(e) => {setCreditItemTitleM1(index, e.target.value); subField.handleChange(e.target.value)}"
                          />
                        </template>
                      </itemsForm.Field>
                    </div>

                    <div v-else>
                      <span>{{ row.title }}</span>
                    </div>
                  </template>

                  <template #totalPrice-data="{ row }">
                    <div>
                      <span>{{ currency(row.totalPrice) }}</span>
                    </div>
                  </template>

                  <template #percentage-data="{ index }">
                    <itemsForm.Field
                      :name="`methodOneItems[${index}].percentage`"
                      :validators="{
                        onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Percentage must be a valid number between -100 and 100', isValidPercentage)
                      }"
                    >
                      <template #default="{ field: subField }">
                        <BaseInput
                          :model-value="(subField.state.value as string)"
                          :name="subField.name"
                          type="number"
                          :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                          @blur="subField.handleBlur"
                          @input="(e) => {setPercentage(index, e.target.value); subField.handleChange(e.target.value)}"
                        />
                      </template>
                    </itemsForm.Field>
                  </template>
                </BaseTable>
              </template>
            </itemsForm.Field>
          </div>

          <div v-if="selectedMethod === '2'">
            <itemsForm.Field name="methodTwoItems">
              <template #default>
                <BaseTable
                  :fields="methodTwoTableFields"
                  :items="itemsForm.getFieldValue('methodTwoItems')"
                >
                  <template #arrow-header>
                    <div class="text-center">
                      <span class="i-heroicons-arrow-right size-4"/>
                    </div>
                  </template>
                  <template #arrow-data>
                    <div class="text-center">
                      <span class="i-heroicons-arrow-right size-4"/>
                    </div>
                  </template>

                  <template #title-data="{ row, index }">
                    <div v-if="row.id.toString().includes('credit')">
                      <itemsForm.Field
                        :name="`methodTwoItems[${index}].title`"
                        :validators="{
                          onBlur: yup.string().required('Title is required')
                        }"
                      >
                        <template #default="{ field: subField }">
                          <BaseInput
                            :model-value="(subField.state.value as string)"
                            :name="subField.name"
                            :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                            @blur="subField.handleBlur"
                            @input="(e) => {setCreditItemTitleM2(index, e.target.value); subField.handleChange(e.target.value)}"
                          />
                        </template>
                      </itemsForm.Field>
                    </div>
                    <div v-else>
                      <span>{{ row.title }}</span>
                    </div>
                  </template>

                  <template #totalPrice-data="{ row }">
                    <div>
                      <span>{{ currency(row.totalPrice) }}</span>
                    </div>
                  </template>

                  <template #amount-data="{ row, index }">
                    <itemsForm.Field
                      :name="`methodTwoItems[${index}].amount`"
                      :validators="{
                        onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number',`Amount must be a valid number, between -${currency(Math.abs(row.totalPrice))} and ${currency(Math.abs(row.totalPrice))}`, (value) => isValidAmount(value, row.totalPrice))
                      }"
                    >
                      <template #default="{ field: subField }">
                        <BaseInputCurrency
                          :model-value="(subField.state.value as string)"
                          :name="subField.name"
                          :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                          @blur="subField.handleBlur"
                          @input="(e) => {setAmount(index, e.target.value); subField.handleChange(e.target.value)}"
                        />
                      </template>
                    </itemsForm.Field>
                  </template>
                </BaseTable>
              </template>
            </itemsForm.Field>
          </div>

          <div v-if="selectedMethod === '3'">
            <itemsForm.Field
              name="percentageWished"
              :validators="{
                onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Percentage must be a valid number between 0 and 100', isValidPositivePercentage)
              }"
            >
              <template #default="{ field }">
                <div class="mt-4">
                  <BaseInput
                    :model-value="(field.state.value as string)"
                    :name="field.name"
                    label="What percentage do you wish to invoice now ?"
                    type="number"
                    :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                    @blur="field.handleBlur"
                    @input="(e) => {setPercentageWished(e.target.value); field.handleChange(e.target.value)}"
                  />
                </div>
              </template>
            </itemsForm.Field>
            <itemsForm.Field
              name="creditPercentageWished"
              :validators="{
                onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Percentage must be a valid number between -100 and 0', isValidCreditPercentage)
              }"
            >
              <template #default="{ field }">
                <div class="mt-4">
                  <BaseInput
                    :model-value="(field.state.value as string)"
                    :name="field.name"
                    label="What percentage do you wish to credit now ?"
                    type="number"
                    :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                    @blur="field.handleBlur"
                    @input="(e) => {setCreditPercentageWished(e.target.value); field.handleChange(e.target.value)}"
                  />
                </div>
              </template>
            </itemsForm.Field>
          </div>
        </form>

        <div v-if="selectedMethod === '4'">
          <BaseTable
            :fields="methodFourTableFields"
            :items="methodOneTableItems.filter(item => !item.id.toString().includes('credit'))"
          >
            <template #title-data="{ row }">
              <div class="flex items-center">
                <BaseCheckbox
                  v-model="selectedItems"
                  class="-mb-4"
                  :value="row.id"
                />

                <span class="ml-2">{{ row.title }}</span>
              </div>
            </template>

            <template #totalPrice-header="{ column }">
              <span class="text-center block">{{ column.label }}</span>
            </template>

            <template #totalPrice-data="{ row }">
              <span class="text-center block">{{ currency(row.totalPrice) }}</span>
            </template>
          </BaseTable>
        </div>
      </template>

      <template #footer>
        <hr class="mx-4 sm:mx-6">
        <div class="flex justify-end gap-2 my-3 mr-4 sm:mr-6">
          <BaseButton
            variant="base"
            text="Cancel"
            @click="showModal = false"
          />

          <BaseButton
            variant="primary"
            text="Confirm"
            :disabled="Math.abs(thisInvoice.value) + Math.abs(invoicedValue.previouslyInvoicedValue) > jobsTotalValue"
            :class="{ 'cursor-not-allowed opacity-50': Math.abs(thisInvoice.value) + Math.abs(invoicedValue.previouslyInvoicedValue) > jobsTotalValue }"
            @click="itemsForm.handleSubmit()"
          />
        </div>
      </template>
    </Modal>

  </div>
</template>

<script lang="ts" setup>
import { Invoice, InvoiceItem, generateInvoiceId } from '@/models/invoice'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import InvoiceService from '@/services/InvoiceService'
import BaseSelect from '@/components/base/BaseSelect.vue'
import { currency, formatDate, toNumber } from '@/utils'
import { useForm } from '@tanstack/vue-form'
import { computed, onMounted, PropType, Ref, ref, watch } from 'vue'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import Modal from '@/components/app/AppModal.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import BudgetService from '@/services/BudgetService'
import { useQuery } from '@tanstack/vue-query'
import { useRoute } from 'vue-router'

type selectOption = {
  label: string
  value: string,
  disabled?: boolean
  [key: string]: any
}

const props = defineProps({
  projectId: {
    type: String,
    default: ''
  },
  projects: {
    type: Array as PropType<selectOption[]>,
    required: true
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const invoice: Ref<Invoice> = ref(new Invoice())
invoice.value.projectId = props.projectId
const backTo = useRoute().query.backTo as string || ''

const showModal = ref(false)

const invoiceItems = ref([])
const percentageWished = ref(0)
const creditPercentageWished = ref(0)
const thisInvoice = ref({
  value: 0,
  absValue: 0,
  credit: 0
})
const jobsTotalValue = ref(0)
const selectedMethod = ref('1')
const selectedItems = ref([])

const selectOptions = ref([
  { value: '1', label: 'Method #1: Invoice percentage of progress (%) for each line item' },
  { value: '2', label: 'Method #2: Invoice certain amount ($) for each line item' },
  { value: '3', label: 'Method #3: Invoice percentage of progress (%) for the entire job and change orders separately' },
  { value: '4', label: 'Method #4: Select items' }
])

const methodOneTableFields = ref([
  { key: 'title', label: 'Title' },
  { key: 'totalPrice', label: 'Contract Value' },
  { key: 'arrow', label: '' },
  { key: 'percentage', label: 'Percentage Complete To This Point' },
])
const methodOneTableItems = ref([] as { id: string, title: string, totalPrice: number, percentage: number }[])

const methodTwoTableFields = ref([
  { key: 'title', label: 'Title' },
  { key: 'totalPrice', label: 'Contract Value' },
  { key: 'arrow', label: '' },
  { key: 'amount', label: '$ to be invoiced now' },
])
const methodTwoTableItems = ref([] as { id: string, title: string, totalPrice: number, amount: number }[])

const methodFourTableFields = ref([
  { key: 'title', label: 'Title' },
  { key: 'totalPrice', label: 'Price' }
])

const form = useForm({
  defaultValues: invoice.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const newInvoice = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as unknown as Invoice
    newInvoice.dueDate = new Date(newInvoice.dueDate)
    newInvoice.invoiceDate = new Date(newInvoice.invoiceDate)

    const customerId = props.projects.find(project => project.value === newInvoice.projectId)?.customerId
    newInvoice.customerId = customerId

    await props.saveFunction(newInvoice)
  },
})

const itemsForm = useForm({
  defaultValues: {
    methodOneItems: methodOneTableItems.value,
    methodTwoItems: methodTwoTableItems.value,
    percentageWished: percentageWished.value,
    creditPercentageWished: creditPercentageWished.value
  },
  validatorAdapter: yupValidator(),
  onSubmit: () => confirmed()
})

const selectedProjectId = ref('')
const isProjectSelected = computed(() => !!selectedProjectId.value)

const budgetService = new BudgetService()

const { data:budgetLineItems, isLoading:lineItemsLoading } = useQuery({
  queryKey: ['LineItems', selectedProjectId],
  queryFn: async () =>  await budgetService.getLineItems(selectedProjectId.value),
  enabled: isProjectSelected
})

const invoiceService = new InvoiceService()
const { data:invoicedValue } = useQuery({
  queryKey: ['invoicedValue', selectedProjectId],
  queryFn: async () =>  await invoiceService.getPreviouslyInvoicedValue(selectedProjectId.value),
  enabled: isProjectSelected
})

const projectSelected = async (lineItems) => {
  resetValues()
  itemsForm.reset()
  methodOneTableItems.value = []
  lineItems.forEach(item => {
    if (item.selected) methodOneTableItems.value.push({ id: item.id, title: item.title, totalPrice: item.totalPrice, percentage: 0 })
  })

  jobsTotalValue.value = lineItems.reduce((acc, item) => {
    if (item.selected) acc += !isNaN(toNumber(item.totalPrice)) ? toNumber(item.totalPrice) : 0
    return acc
  }, 0)

  // push a credit item
  const previouslyInvoicedValue = invoicedValue.value ? invoicedValue.value.previouslyInvoicedValue : (await invoiceService.getPreviouslyInvoicedValue(selectedProjectId.value)).previouslyInvoicedValue
  const balance = jobsTotalValue.value - Math.abs(previouslyInvoicedValue)
  methodOneTableItems.value.push({ id: `${new Date().getTime()}-credit`, title: 'Credit', totalPrice: balance, percentage: 0 })
  itemsForm.useStore((state) => state.values.methodOneItems = [...methodOneTableItems.value])
  selectedMethod.value = '1'

  const invoiceDate = new Date()
  const dueDate = new Date()
  dueDate.setDate(invoiceDate.getDate() + 15)

  if (!form.getFieldValue('invoiceDate')) {
    form.setFieldValue('invoiceDate', formatDate(invoiceDate) as any)
    form.validateField('invoiceDate', 'blur')
  }

  if (!form.getFieldValue('dueDate')) {
    form.setFieldValue('dueDate', formatDate(dueDate) as any)
    form.validateField('dueDate', 'blur')
  }

  jobsTotalValue.value = lineItems.reduce((acc, item) => {
    if (item.selected) acc += !isNaN(toNumber(item.totalPrice)) ? toNumber(item.totalPrice) : 0
    return acc
  }, 0)

  if (!form.getFieldValue('invoiceId')) {
    form.setFieldValue('invoiceId', await generateInvoiceId())
    form.validateField('invoiceId', 'blur')
  }
}

const methodOneAmountCalcul = () => {
  thisInvoice.value.value = 0
  thisInvoice.value.absValue = 0
  thisInvoice.value.credit = 0

  const methodOneItems = itemsForm.getFieldValue('methodOneItems')
  methodOneItems.forEach(item => {
    if (isNaN(parseFloat(String(item.percentage))) || parseFloat(String(item.percentage)) === 0) {
      return
    }

    if (item.id.toString().includes('credit')) {
      const amount = toNumber(item.totalPrice) * parseFloat(String(item.percentage)) / 100
      thisInvoice.value.value += amount
      thisInvoice.value.absValue += Math.abs(amount)
      thisInvoice.value.credit += amount < 0 ? Math.abs(amount) : 0
      return
    }

    const budgetItem = budgetLineItems.value.find(budgetItem => budgetItem.id === item.id)
    if (!budgetItem) return
    thisInvoice.value.value += toNumber(budgetItem.totalPrice) * parseFloat(String(item.percentage)) / 100
    thisInvoice.value.absValue += Math.abs(toNumber(budgetItem.totalPrice) * parseFloat(String(item.percentage)) / 100)
    thisInvoice.value.credit += (toNumber(budgetItem.totalPrice) * parseFloat(String(item.percentage)) / 100) < 0 ? Math.abs(toNumber(budgetItem.totalPrice) * parseFloat(String(item.percentage)) / 100) : 0
  })
}

const methodTwoAmountCalcul = () => {
  thisInvoice.value.value = 0
  thisInvoice.value.absValue = 0
  thisInvoice.value.credit = 0
  const methodTwoItems = itemsForm.getFieldValue('methodTwoItems')
  methodTwoItems.forEach(item => {
    if (isNaN(toNumber(item.amount)) || parseFloat(item.amount as any) === 0) {
      return
    }
    thisInvoice.value.value += toNumber(item.amount)
    thisInvoice.value.absValue += Math.abs(toNumber(item.amount))
    thisInvoice.value.credit += toNumber(item.amount) < 0 ? Math.abs(toNumber(item.amount)) : 0
  })
}

const methodThreeAmountCalcul = () => {
  thisInvoice.value.value = 0
  thisInvoice.value.absValue = 0
  thisInvoice.value.credit = 0

  if (isNaN(parseFloat(String(itemsForm.getFieldValue('percentageWished')))) && isNaN(parseFloat(String(itemsForm.getFieldValue('creditPercentageWished'))))) {
    return
  }

  if (!isNaN(parseFloat(String(itemsForm.getFieldValue('percentageWished')))) && parseFloat(String(itemsForm.getFieldValue('percentageWished'))) !== 0) {
    methodOneTableItems.value.forEach(item => {
      const budgetItem = budgetLineItems.value.find(lineItem => lineItem.id === item.id)
      if (!budgetItem) return

      thisInvoice.value.value += toNumber(budgetItem.totalPrice) * parseFloat(String(itemsForm.getFieldValue('percentageWished'))) / 100
      thisInvoice.value.absValue += Math.abs(toNumber(budgetItem.totalPrice) * parseFloat(String(itemsForm.getFieldValue('percentageWished'))) / 100)
      thisInvoice.value.credit += (toNumber(budgetItem.totalPrice) * parseFloat(String(itemsForm.getFieldValue('percentageWished'))) / 100) < 0 ? Math.abs(toNumber(budgetItem.totalPrice) * parseFloat(String(itemsForm.getFieldValue('percentageWished'))) / 100) : 0
    })
  }

  if (!isNaN(parseFloat(String(itemsForm.getFieldValue('creditPercentageWished')))) && parseFloat(String(itemsForm.getFieldValue('creditPercentageWished'))) !== 0) {
    methodOneTableItems.value.forEach(item => {
      const budgetItem = budgetLineItems.value.find(lineItem => lineItem.id === item.id)
      if (!budgetItem) return

      thisInvoice.value.value += toNumber(budgetItem.totalPrice) * parseFloat(String(itemsForm.getFieldValue('creditPercentageWished'))) / 100
      thisInvoice.value.absValue += Math.abs(toNumber(budgetItem.totalPrice) * parseFloat(String(itemsForm.getFieldValue('creditPercentageWished'))) / 100)
      thisInvoice.value.credit += (toNumber(budgetItem.totalPrice) * parseFloat(String(itemsForm.getFieldValue('creditPercentageWished'))) / 100) < 0 ? Math.abs(toNumber(budgetItem.totalPrice) * parseFloat(String(itemsForm.getFieldValue('creditPercentageWished'))) / 100) : 0
    })
  }
}

const methodFourAmountCalcul = () => {
  thisInvoice.value.value = 0
  thisInvoice.value.absValue = 0
  thisInvoice.value.credit = 0
  selectedItems.value.forEach(id => {
    const budgetItem = budgetLineItems.value.find(lineItem => lineItem.id === id)

    if (!budgetItem) return

    thisInvoice.value.value += toNumber(budgetItem.totalPrice)
    thisInvoice.value.absValue += Math.abs(toNumber(budgetItem.totalPrice))
    thisInvoice.value.credit += toNumber(budgetItem.totalPrice) < 0 ? Math.abs(toNumber(budgetItem.totalPrice)) : 0
  })
}

watch(selectedMethod, (newVal) => {
  resetValues()
  itemsForm.reset()
  if (newVal === '1') {
    methodOneTableItems.value = []
    budgetLineItems.value.forEach(item => {
      if (item.selected) methodOneTableItems.value.push({ id: item.id, title: item.title, totalPrice: item.totalPrice, percentage: 0 })
    })
    // push a credit item
    const previouslyInvoicedValue = invoicedValue.value.previouslyInvoicedValue
    const balance = jobsTotalValue.value - Math.abs(previouslyInvoicedValue)
    methodOneTableItems.value.push({ id: `${new Date().getTime()}-credit`, title: 'Credit', totalPrice: balance, percentage: 0 })
    itemsForm.useStore((state) => state.values.methodOneItems = [...methodOneTableItems.value])
  } else if (newVal === '2') {
    methodTwoTableItems.value = []
    budgetLineItems.value.forEach(item => {
      if (item.selected) methodTwoTableItems.value.push({ id: item.id, title: item.title, totalPrice: item.totalPrice, amount: 0 })
    })
    // push a credit item
    const previouslyInvoicedValue = invoicedValue.value.previouslyInvoicedValue
    const balance = jobsTotalValue.value - Math.abs(previouslyInvoicedValue)
    methodTwoTableItems.value.push({ id: `${new Date().getTime()}-credit`, title: 'Credit', totalPrice: balance, amount: 0 })
    itemsForm.useStore((state) => state.values.methodTwoItems = [...methodTwoTableItems.value])
  }
})

watch(selectedItems, () => {
  methodFourAmountCalcul()
})

const deleteMethodOneItems = () => {
  const methodOneItems = itemsForm.getFieldValue('methodOneItems') || []

  methodOneItems.forEach((_, index) => {
    itemsForm.useStore((state) => state.values.methodOneItems[index] && (state.values.methodOneItems[index].percentage = 0))
    itemsForm.validateField(`methodOneItems[${index}].percentage`, 'change')
  })
  itemsForm.deleteField('methodOneItems')
}

const deleteMethodTwoItems = () => {
  const methodTwoItems = itemsForm.getFieldValue('methodTwoItems') || []

  methodTwoItems.forEach((_, index) => {
    itemsForm.useStore((state) => state.values.methodTwoItems[index] && (state.values.methodTwoItems[index].amount = 0))
    itemsForm.validateField(`methodTwoItems[${index}].amount`, 'change')
  })
  itemsForm.deleteField('methodTwoItems')
}

const deletePercentageWished = () => {
  itemsForm.useStore((state) => state.values.percentageWished = 0)
  itemsForm.validateField('percentageWished', 'change')
  itemsForm.deleteField('percentageWished')
  itemsForm.useStore((state) => state.values.creditPercentageWished = 0)
  itemsForm.validateField('creditPercentageWished', 'change')
  itemsForm.deleteField('creditPercentageWished')
}

const isValidPercentage = (value: string) => {
  if(!value) return true
  return !isNaN(parseFloat(value)) && parseFloat(value) >= -100 && parseFloat(value) <= 100
}

const isValidCreditPercentage = (value: string) => {
  if(!value) return true
  return !isNaN(parseFloat(value)) && parseFloat(value) >= -100 && parseFloat(value) <= 0
}

const isValidPositivePercentage = (value: string) => {
  if(!value) return true
  return !isNaN(parseFloat(value)) && parseFloat(value) >= 0 && parseFloat(value) <= 100
}

const isValidAmount = (value: string, total: number) => {
  if(!value) return true

  return !isNaN(toNumber(value)) && toNumber(value) >= -Math.abs(total) && toNumber(value) <= Math.abs(total)
}

const setPercentage = (index: number, value) => {
  itemsForm.useStore((state) => state.values.methodOneItems[index] && (state.values.methodOneItems[index].percentage = value))
  methodOneAmountCalcul()
}

const setCreditItemTitleM1 = (index: number, value) => {
  itemsForm.useStore((state) => state.values.methodOneItems[index] && (state.values.methodOneItems[index].title = value))
}

const setCreditItemTitleM2 = (index: number, value) => {
  itemsForm.useStore((state) => state.values.methodTwoItems[index] && (state.values.methodTwoItems[index].title = value))
}

const setAmount = (index: number, value) => {
  itemsForm.useStore((state) => state.values.methodTwoItems[index] && (state.values.methodTwoItems[index].amount = value))
  methodTwoAmountCalcul()
}

const setPercentageWished = (value) => {
  itemsForm.useStore((state) => state.values.percentageWished = value)
  methodThreeAmountCalcul()
}

const setCreditPercentageWished = (value) => {
  itemsForm.useStore((state) => state.values.creditPercentageWished = value)
  methodThreeAmountCalcul()
}

const confirmed = () => {
  invoiceItems.value = []
  invoice.value.items = []
  form.setFieldValue('items', [])
  form.setFieldValue('total', 0)
  form.setFieldValue('credit', 0)
  form.setFieldValue('balance', 0)

  if (selectedMethod.value === '1') {
    const methodOneItems = itemsForm.getFieldValue('methodOneItems')
    methodOneItems.forEach(item => {
      if (isNaN(parseFloat(String(item.percentage))) || parseFloat(String(item.percentage)) === 0) {
        return
      }

      if (item.id.toString().includes('credit')) {
        const amount = toNumber(item.totalPrice) * parseFloat(String(item.percentage)) / 100
        invoiceItems.value.push({
          ...item,
          scopeOfWork: `<p><b>${item.percentage}% Credit (${currency(amount)})</b></p>`,
          quantity: 1,
          unitPrice: item.totalPrice,
          totalPrice: amount
        })
        if (amount < 0) {
          form.setFieldValue('credit', Math.abs(toNumber(form.getFieldValue('credit'))) + Math.abs(amount))
        } else {
          form.setFieldValue('total', toNumber(form.getFieldValue('total')) + Math.abs(amount))
        }
        form.setFieldValue('balance', toNumber(form.getFieldValue('total')) - toNumber(form.getFieldValue('credit')))
      } else {
        const budgetItem = budgetLineItems.value.find(lineItem => lineItem.id === item.id)
        const amountToInvoice = toNumber(budgetItem.totalPrice) * parseFloat(String(item.percentage)) / 100

        invoiceItems.value.push({
          id: budgetItem.id,
          title: budgetItem.title,
          scopeOfWork: `${budgetItem.scopeOfWork}<br><br> <p><b>${item.percentage}% ${amountToInvoice >= 0 ? 'Completed' : 'Credit' } (${currency(amountToInvoice)})</b></p>`,
          quantity: budgetItem.quantity,
          unitPrice: budgetItem.unitPrice,
          totalPrice: amountToInvoice
        })
        if (amountToInvoice < 0) {
          form.setFieldValue('credit', Math.abs(toNumber(form.getFieldValue('credit'))) + Math.abs(amountToInvoice))
        } else {
          form.setFieldValue('total', toNumber(form.getFieldValue('total')) + Math.abs(amountToInvoice))
        }
        form.setFieldValue('balance', toNumber(form.getFieldValue('total')) - toNumber(form.getFieldValue('credit')))
      }
    })
  } else if (selectedMethod.value  === '2') {
    const methodTwoItems = itemsForm.getFieldValue('methodTwoItems')
    methodTwoItems.forEach(item => {
      if (isNaN(parseFloat(String(item.amount))) || parseFloat(String(item.amount)) === 0) {
        return
      }

      if (item.id.toString().includes('credit')) {
        invoiceItems.value.push({
          ...item,
          scopeOfWork: `<p><b>Credit for this invoice ${currency(item.amount)}</b></p>`,
          quantity: 1,
          unitPrice: item.totalPrice,
          totalPrice: toNumber(item.amount)
        })
        if (toNumber(item.amount) < 0) {
          form.setFieldValue('credit', Math.abs(toNumber(form.getFieldValue('credit'))) + Math.abs(toNumber(item.amount)))
        } else {
          form.setFieldValue('total', toNumber(form.getFieldValue('total')) + Math.abs(toNumber(item.amount)))
        }
        form.setFieldValue('balance', toNumber(form.getFieldValue('total')) - toNumber(form.getFieldValue('credit')))
      } else {

        const budgetItem = budgetLineItems.value.find(lineItem => lineItem.id === item.id)

        invoiceItems.value.push({
          id: budgetItem.id,
          title: budgetItem.title,
          scopeOfWork: `${budgetItem.scopeOfWork}<br><br> <p><b>${item.amount >= 0 ? 'Amount to invoice for this work' : 'Credit for this work' } ${currency(item.amount)}</b></p>`,
          quantity: budgetItem.quantity,
          unitPrice: budgetItem.unitPrice,
          totalPrice: toNumber(item.amount)
        })

        if (toNumber(item.amount) < 0) {
          form.setFieldValue('credit', Math.abs(toNumber(form.getFieldValue('credit'))) + Math.abs(toNumber(item.amount)))
        } else {
          form.setFieldValue('total', toNumber(form.getFieldValue('total')) + Math.abs(toNumber(item.amount)))
        }
        form.setFieldValue('balance', toNumber(form.getFieldValue('total')) - toNumber(form.getFieldValue('credit')))
      }
    })
  } else if (selectedMethod.value  === '3') {
    const percentage = itemsForm.getFieldValue('percentageWished')
    const creditPercentageWished = itemsForm.getFieldValue('creditPercentageWished')

    if (isNaN(parseFloat(String(percentage))) && isNaN(parseFloat(String(creditPercentageWished)))) {
      return
    }

    if (parseFloat(String(percentage)) > 0) {
      const methodOneItems = itemsForm.getFieldValue('methodOneItems')
      methodOneItems.forEach(item => {
        const budgetItem = budgetLineItems.value.find(lineItem => lineItem.id === item.id)
        if (!budgetItem) return

        const amountToInvoice = toNumber(budgetItem.totalPrice) * parseFloat(String(percentage)) / 100

        invoiceItems.value.push({
          id: budgetItem.id,
          title: budgetItem.title,
          scopeOfWork: `${budgetItem.scopeOfWork}<br><br> <p><b>${percentage}% completed (${currency(amountToInvoice)})</b></p>`,
          quantity: budgetItem.quantity,
          unitPrice: budgetItem.unitPrice,
          totalPrice: amountToInvoice
        })

        form.setFieldValue('total', toNumber(form.getFieldValue('total')) + Math.abs(amountToInvoice))
        form.setFieldValue('balance', toNumber(form.getFieldValue('total')) - toNumber(form.getFieldValue('credit')))
      })
    }

    // for credit
    if (parseFloat(String(creditPercentageWished)) < 0) {
      const amount = thisInvoice.value.credit
      invoiceItems.value.push({
        id: `${new Date().getTime()}-credit`,
        title: 'Credit',
        scopeOfWork: `<p><b>Credit for this invoice ${currency(amount)} (${creditPercentageWished}%)</b></p>`,
        quantity: 1,
        unitPrice: -1 * thisInvoice.value.credit,
        totalPrice: -1 * thisInvoice.value.credit
      })

      form.setFieldValue('credit', Math.abs(toNumber(form.getFieldValue('credit'))) + Math.abs(amount))
      form.setFieldValue('balance', toNumber(form.getFieldValue('total')) - toNumber(form.getFieldValue('credit')))
    }
  } else if (selectedMethod.value === '4') {
    selectedItems.value.forEach(id => {
      const budgetItem = budgetLineItems.value.find(lineItem => lineItem.id === id)

      if (!budgetItem) return

      invoiceItems.value.push({
        id: budgetItem.id,
        title: budgetItem.title,
        scopeOfWork: budgetItem.scopeOfWork,
        quantity: budgetItem.quantity,
        unitPrice: budgetItem.unitPrice,
        totalPrice: toNumber(budgetItem.totalPrice)
      })

      if (toNumber(budgetItem.totalPrice) < 0) {
        form.setFieldValue('credit', Math.abs(toNumber(form.getFieldValue('credit'))) + Math.abs(toNumber(budgetItem.totalPrice)))
      } else {
        form.setFieldValue('total', toNumber(form.getFieldValue('total')) + Math.abs(toNumber(budgetItem.totalPrice)))
      }
      form.setFieldValue('balance', toNumber(form.getFieldValue('total')) - toNumber(form.getFieldValue('credit')))
    })
  }

  fillInvoiceItems()
  showModal.value = false
}

const fillInvoiceItems = () => {
  const items = []
  invoiceItems.value.forEach(item => {
    const invoiceItem = new InvoiceItem()
    invoiceItem.id = item.id
    invoiceItem.title = item.title
    invoiceItem.description = item.scopeOfWork
    invoiceItem.quantity = item.quantity
    invoiceItem.unitPrice = item.unitPrice
    invoiceItem.total = item.totalPrice

    items.push(invoiceItem)
  })

  form.setFieldValue('items', JSON.parse(JSON.stringify(items)))
  form.validateField('items', 'blur')
}

const resetValues = () => {
  invoiceItems.value = []
  thisInvoice.value.value = 0
  thisInvoice.value.absValue = 0
  thisInvoice.value.credit = 0
  selectedItems.value = []

  // reset invoice form values
  invoice.value.items = []
  invoice.value.total = 0
  invoice.value.credit = 0
  invoice.value.balance = 0
  form.setFieldValue('credit', 0)
  form.setFieldValue('total', 0)
  form.setFieldValue('balance', 0)

  // reset items form values
  deleteMethodOneItems()
  deleteMethodTwoItems()
  deletePercentageWished()
}

onMounted( async () => {

  if (props.projectId) {
    selectedProjectId.value = props.projectId
    form.validateField('projectId', 'blur')
  }

  if (!form.getFieldValue('invoiceId')) {
    form.setFieldValue('invoiceId', await generateInvoiceId())
    form.validateField('invoiceId', 'blur')
  }
})

watch(() => [lineItemsLoading.value, selectedProjectId.value], ([newVal]) => {
  if (!newVal && budgetLineItems.value) {
    projectSelected(budgetLineItems.value)
  }
}, { immediate: true })
</script>
